<script setup>
import { reactiveOmit } from "@vueuse/core";
import { IconX } from "@tabler/icons-vue";
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from "reka-ui";
import { cn } from "@/lib/utils";

const props = defineProps({
  forceMount: { type: Boolean, required: false },
  trapFocus: { type: Boolean, required: false },
  disableOutsidePointerEvents: { type: Boolean, required: false },
  asChild: { type: Boolean, required: false },
  as: { type: null, required: false },
  class: { type: null, required: false },
  hideCloseButton: { type: Boolean, required: false },
});
const emits = defineEmits([
  "escapeKeyDown",
  "pointerDownOutside",
  "focusOutside",
  "interactOutside",
  "openAutoFocus",
  "closeAutoFocus",
]);

const delegatedProps = reactiveOmit(props, "class");
const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="
        cn(
          'vf-fixed vf-inset-0 vf-z-[99] vf-bg-black/50 data-[state=open]:vf-animate-in data-[state=closed]:vf-animate-out',
          'data-[state=closed]:vf-fade-out-0 data-[state=open]:vf-fade-in-0'
        )
      "
    />
    <DialogContent
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'vf-fixed vf-left-1/2 vf-top-1/2 vf-z-[100] vf-grid vf-w-full vf-max-w-lg -vf-translate-x-1/2 -vf-translate-y-1/2',
          'vf-gap-4 vf-border vf-bg-background vf-p-6 vf-shadow-lg vf-duration-200 data-[state=open]:vf-animate-in',
          'data-[state=closed]:vf-animate-out data-[state=closed]:vf-fade-out-0 data-[state=open]:vf-fade-in-0 ',
          'data-[state=closed]:vf-zoom-out-95 data-[state=open]:vf-zoom-in-95 data-[state=closed]:vf-slide-out-to-left-1/2',
          'data-[state=closed]:vf-slide-out-to-top-[48%] data-[state=open]:vf-slide-in-from-left-1/2 data-[state=open]:vf-slide-in-from-top-[48%] sm:vf-rounded-lg',
          props.class
        )
      "
    >
      <slot />

      <DialogClose
        :class="
          cn(
            'vf-absolute vf-right-4 vf-top-4 vf-rounded-sm vf-opacity-70 vf-ring-offset-background vf-transition-opacity hover:vf-opacity-100',
            'focus:vf-outline-none focus:vf-ring-2 focus:vf-ring-ring focus:vf-ring-offset-2 disabled:vf-pointer-events-none',
            'data-[state=open]:vf-bg-accent data-[state=open]:vf-text-muted-foreground'
          )
        "
        v-if="!props.hideCloseButton"
      >
        <IconX />
        <span class="vf-sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
